import { motion } from "framer-motion";
import { Award, Star, Quote } from "lucide-react";
import { significantAchievements } from "../data/profile";
import { Reveal, Overline, AnimatedHeading } from "./Reveal";
import { AchievementGallery } from "./AchievementGallery";

const featured = significantAchievements.filter((a) => a.featured);
const rest = significantAchievements.filter((a) => !a.featured);

export const Achievements = () => (
  <section id="achievements" className="relative py-16 sm:py-20 lg:py-28 bg-navy text-bone overflow-hidden">
    {/* decorative layers */}
    <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-teal/15 blur-3xl pointer-events-none animate-blob" />
    <div className="absolute bottom-0 -left-24 h-80 w-80 rounded-full bg-gold/10 blur-3xl pointer-events-none animate-blob" style={{ animationDelay: "5s" }} />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-3xl">
        <span className="overline" style={{ color: "#C5A35A" }}>Career Milestones</span>
        <AnimatedHeading className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-tight">
          Significant Achievements
        </AnimatedHeading>
        <p className="mt-5 text-base sm:text-lg text-bone/70 leading-relaxed">
          Two decades of national and international recognition — a record of pioneering accreditations,
          award-winning public health campaigns and transformative healthcare leadership.
        </p>
      </Reveal>

      {/* Featured marquee cards */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
        {featured.map((a, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`featured-achievement-${i}`}
            className="group relative rounded-2xl p-6 sm:p-8 bg-white/[0.04] border border-white/10 hover:border-gold/50 hover:bg-white/[0.07] transition-all duration-300 overflow-hidden"
          >
            <div className="absolute top-0 right-0 h-24 w-24 bg-gold/10 blur-2xl rounded-full group-hover:bg-gold/20 transition-colors" />
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/15 text-gold text-xs font-plex tracking-widest uppercase">
                <Star size={12} className="fill-gold" /> Featured
              </span>
              <span className="font-display text-2xl font-bold text-gold">{a.year}</span>
            </div>
            <h3 className="mt-5 font-display text-xl sm:text-2xl font-bold leading-snug group-hover:text-gold transition-colors">
              {a.title}
            </h3>
            <p className="mt-2 text-sm font-plex uppercase tracking-wider text-teal/90">{a.body}</p>
            <p className="mt-4 text-sm text-bone/70 leading-relaxed">{a.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Timeline list of remaining achievements */}
      <div className="mt-14 sm:mt-16">
        <div className="flex items-center gap-3 mb-8">
          <Quote size={20} className="text-gold" />
          <h3 className="font-display text-xl sm:text-2xl font-bold">More Honours & Recognitions</h3>
        </div>

        <div className="relative pl-6 sm:pl-8">
          <div className="absolute left-[5px] sm:left-[7px] top-2 bottom-2 w-px bg-white/15" />
          <div className="space-y-7">
            {rest.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
                data-testid={`achievement-row-${i}`}
                className="relative group"
              >
                <span className="absolute -left-[1.4rem] sm:-left-[1.65rem] top-1.5 h-3 w-3 rotate-45 bg-gold shrink-0 transition-all duration-300 group-hover:scale-150 group-hover:rotate-[225deg]" />
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4">
                  <span className="font-display text-lg font-bold text-gold shrink-0 sm:w-20">{a.year}</span>
                  <div className="min-w-0">
                    <h4 className="font-display text-lg font-bold leading-snug group-hover:text-gold transition-colors">{a.title}</h4>
                    <p className="text-xs font-plex uppercase tracking-wider text-teal/90 mt-0.5">{a.body}</p>
                    <p className="text-sm text-bone/65 mt-1.5 leading-relaxed">{a.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Certificates & awards gallery from records */}
      <AchievementGallery />

      {/* footer accent */}
      <Reveal delay={0.1} className="mt-14">
        <div className="flex items-center justify-center gap-3 text-bone/60">
          <Award size={18} className="text-gold" />
          <p className="text-sm font-plex tracking-wide text-center">
            National Award Winner in Healthcare Quality · NABH Assessor · Quality Council of India Member
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
